import React, { useState, useEffect } from "react";
import {
  Drawer,
  Form,
  Input,
  Button,
  message,
  Row,
  Col,
  Tooltip,
  Typography,
  Table,
  Tag,
  Spin,
  Upload,
  Image,
  Divider,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@apollo/client";
import { UPDATE_BANNER, GET_PRODUCTS } from "./requests";
import { bannerTypes, productTypes } from "../../../../types";
import ProductList from "../../../../components/productsList";
import useUpload from "../../../../hooks/use-upload";

const { Text } = Typography;

interface CreateBannerProps {
  showModal: boolean;
  setShowModal: (visible: boolean) => void;
  refetch: () => void;
  banner: typeof bannerTypes.banner | undefined;
  setBanner: (banner: typeof bannerTypes.banner | undefined) => void;
}

const EditBanner: React.FC<CreateBannerProps> = ({ showModal, setShowModal, refetch, banner, setBanner }) => {
  const [form] = Form.useForm();
  const { upload } = useUpload();
  const [loading, setLoading] = useState(false)
  const [searchTitle, setSearchTitle] = useState("");
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [pageConfig, setPageConfig] = useState({ page: 1,
    pageSize: 10 });
  const [uploadLoding, setUploadLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const [updateBanner] = useMutation(UPDATE_BANNER);

  const { data, loading: loadingProducts } = useQuery(GET_PRODUCTS, { variables: { params: pageConfig,
    name:searchTitle } });
  const products = data?.products.results || [];

  useEffect(() => {
    if (banner) {
      form.setFieldsValue({
        title: banner.title,
        type: banner.type,
      });
      setImageUrl(banner?.img ?? null)
      setSelectedProducts(banner?.products?.map((p) => p.id) ?? []);
    }
  }, [banner, form]);

  const handleSubmit = async (values: typeof bannerTypes.banner) => {
    setLoading(true);
    try {

      const payload = {
        variables: {
          id: banner?.id,
          banner: {
            ...values,
            img: imageUrl,
            products: selectedProducts,
          },
        },
      }

      await updateBanner(payload);
      await refetch();

      message.success("Banner actualizado exitosamente");
      onClose();
    } catch (error) {
      console.log(error);
      message.error("Error al actualizar el banner");
    } finally {
      setLoading(false);
    }
  };

  const onClose = () => {
    setShowModal(false);
    setSearchTitle("");
    setSelectedProducts([]);
    setBanner(undefined)
  }

  const handleFileUpload = async ({ fileList }) => {
    if (fileList[0]) {
      const fileName = `banners/${new Date().toISOString()}/${
        fileList[0]?.name
      }`;
      setUploadLoading(true);
      const url = await upload(fileList[0].originFileObj, fileName);

      setImageUrl(url);
    }
    setUploadLoading(false);
    message.success("Imagen actualizada.");
  };

  const handleSelect = (record: typeof bannerTypes.banner) => {
    const keys = [...selectedProducts];
    if (keys.includes(record.id)) {
      setSelectedProducts(keys.filter((key) => key !== record.id));
    } else {
      setSelectedProducts([...keys, record.id]);
    }
  };

  const rowSelection = {
    selectedRowKeys: selectedProducts,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedProducts(selectedRowKeys as string[]);
    },
    preserveSelectedRowKeys: true,
  };

  const columns = [
    {
      title: "Nombre",
      key: "name",
      render: (row: typeof productTypes.product) => {
        return <Text strong>{row.name}</Text>;
      },
    },
    {
      title: "Descripción",
      key: "description",
      render: (row: typeof productTypes.product) => {
        return <Text strong>{row.description}</Text>;
      },
    },
    {
      title: 'Variantes',
      key: 'variants',
      dataIndex: 'variants',
      render: (variants: typeof productTypes.product['variants']) => {

        const lineItems = variants?.map((p) => ({
          product: {
            _id: p.id,
            img: p.img,
            title: p.title,
            description: p.description,
            product: {
              name: p.product.name,
              description: p.product.description,
            },
          },
        })) ?? [];

        if(lineItems.length > 0){
          return <ProductList lineItems={lineItems} />;
        } else {
          return <Tag color="yellow">Sin Productos</Tag>
        }
      },
    },
  ];

  return (
    <Drawer
      title="Crear Nuevo Banner"
      open={showModal}
      onClose={onClose}
      width={"60%"}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item name="title" label="Título" rules={[{ required: true,
          message: "Este campo es obligatorio" }]}>
          <Input placeholder="Ingrese el título del banner" />
        </Form.Item>

        <Form.Item name="type" label="Tipo" rules={[{ required: true,
          message: "Este campo es obligatorio" }]}>
          <Input placeholder="Ingrese el tipo de banner" />
        </Form.Item>

        <Text>Cambiar imagen</Text>
        <Upload
          beforeUpload={() => false}
          listType="picture"
          maxCount={1}
          accept="image/*"
          onChange={(info) => {
            handleFileUpload(info);
          }}
          showUploadList={false}
        >
          <Button style={{ marginLeft: 10 }} icon={<UploadOutlined />}>Seleccionar Imagen</Button>
          {uploadLoding ? <Spin style={{ margin: 10 }} /> : null}
        </Upload>
        {imageUrl && (
          <Image style={{ marginLeft: 20 }} width={140} src={imageUrl} />
        )}
        <Divider />
        <Row>
          <Col span={18}>
            <Tooltip title="Buscar Producto por Nombre">
              <Input
                placeholder="Buscar Producto por Nombre"
                value={searchTitle}
                onChange={(e) => setSearchTitle(e.target.value)}
                allowClear
              />
            </Tooltip>
          </Col>
          <Col span={6}>
            <Text style={{ marginLeft:10,
              fontSize:"15px" }} strong>Productos Seleccionados: {selectedProducts.length}</Text>
          </Col>
        </Row>
        <div style={{ marginTop: 10 }}>
          <Table<typeof bannerTypes.banner>
            dataSource={products}
            rowKey="id"
            loading={loadingProducts}
            pagination={{
              current: Number(pageConfig.page),
              defaultCurrent: pageConfig.page,
              pageSize: Number(pageConfig.pageSize),
              defaultPageSize: pageConfig.pageSize,
              total: data?.products?.info?.count,
              showTotal: (total) => (
                <Text disabled>{`${total} resultados`}</Text>
              ),
              onChange: (page, pageSize) => {
                setPageConfig({
                  page,
                  pageSize,
                });
              },
              showSizeChanger: true,
            }}
            rowSelection={rowSelection}
            onRow={(record) => ({
              onClick: () => handleSelect(record),
            })}
            columns={columns}
          />
        </div>
        <Form.Item>
          <Button size="large" type="primary" htmlType="submit" loading={loading}>
            Actualizar Banner
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default EditBanner;
